// src/services/retargeting/gtm.service.ts

/**
 * Google Ads Conversion Tracking Service
 * Centralized Google Ads tracking with cookie consent support
 */

export enum GTMConversionType {
    PAGE_VIEW = 'AW-17603418624/xP6xCMfP46cbEIC0-8lB',
    FIRST_MESSAGE = 'AW-17603418624/LNNDCNLA7acbEIC0-8lB',
    HIGH_INTENT_LEAD = 'AW-17603418624/NV0tCI_w5qcbEIC0-8lB',
    ASSISTANT_SELECTED = 'AW-17603418624/FGmrCMj05KcbEIC0-8lB',
}

export interface GTMConversionParams {
    value?: number;
    currency?: string;
    transaction_id?: string;
    event_category?: string;
    event_label?: string;
    [key: string]: any;
}

declare global {
    interface Window {
        gtag: {
            (command: 'js', date: Date): void;
            (command: 'config', targetId: string, config?: any): void;
            (command: 'event', eventName: string, eventParams?: any): void;
            (...args: any[]): void;  // Add this generic overload
        };
        gtagStub: (...args: any[]) => void;
        gtagQueue: any[];
        dataLayer: any[];
    }
}

class GTMService {
    private static instance: GTMService;
    private googleAdsId = 'AW-17603418624';
    private isInitialized = false;
    private scriptLoaded = false;

    private constructor() {}

    static getInstance(): GTMService {
        if (!GTMService.instance) {
            GTMService.instance = new GTMService();
        }
        return GTMService.instance;
    }

    /**
     * Check if user has consented to cookies
     */
    private getCookieConsent(): string | null {
        try {
            return localStorage.getItem('cookieConsent');
        } catch (error) {
            console.warn('localStorage not available:', error);
            return null;
        }
    }

    /**
     * Initialize Google Ads Tracking
     */
    initialize(): void {
        console.log('Google Ads tracking initialize() called');

        if (this.isInitialized) {
            console.log('Google Ads already initialized, skipping');
            return;
        }

        if (typeof window === 'undefined') {
            console.log('Window is undefined, skipping Google Ads init');
            return;
        }

        const consent = this.getCookieConsent();
        console.log('Cookie consent status:', consent);

        if (consent === 'accepted') {
            console.log('Consent accepted, loading Google Ads');
            this.loadGoogleAds();
        } else {
            console.log('No consent yet, initializing stub');
            // Use stub until consent is given
            this.initializeStub();
            this.listenForConsent();
        }

        this.isInitialized = true;
        console.log('Google Ads initialization complete');
    }

    /**
     * Initialize stub for queuing events before consent
     */
    private initializeStub(): void {
        window.dataLayer = window.dataLayer || [];
        window.gtagQueue = [];

        window.gtagStub = function(...args: any[]) {
            window.gtagQueue.push(args);
        };

        window.gtag = window.gtagStub as any;
        console.log('Google Ads stub initialized, events will be queued');
    }

    /**
     * Load Google Ads script and initialize tracking
     */
    private loadGoogleAds(): void {
        if (this.scriptLoaded) {
            console.log('Google Ads script already loaded');
            return;
        }

        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];

        // Define gtag function
        window.gtag = function() {
            window.dataLayer.push(arguments);
        };

        // Initialize gtag with current timestamp
        window.gtag('js', new Date());

        // Configure Google Ads with enhanced settings
        window.gtag('config', this.googleAdsId, {
            'allow_ad_personalization_signals': true,
            'send_page_view': true
        });

        console.log('Google Ads dataLayer and gtag initialized');

        // Load gtag.js script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAdsId}`;

        script.onload = () => {
            this.scriptLoaded = true;
            console.log('Google Ads gtag.js script loaded successfully');

            // Process any queued events
            if (window.gtagQueue && window.gtagQueue.length > 0) {
                console.log(`Processing ${window.gtagQueue.length} queued events`);
                window.gtagQueue.forEach((args) => {
                    window.gtag(...args);
                });
                window.gtagQueue = [];
            }
        };

        script.onerror = () => {
            console.error('Failed to load Google Ads gtag.js script');
        };

        document.head.appendChild(script);
        console.log('Google Ads script tag added to document head');
    }

    /**
     * Listen for cookie consent events
     */
    private listenForConsent(): void {
        window.addEventListener('cookieConsentAccepted', () => {
            console.log('Cookie consent accepted event received');
            if (!this.scriptLoaded) {
                this.loadGoogleAds();
            }
        });
    }

    /**
     * Track a conversion event
     */
    trackConversion(
        conversionType: GTMConversionType,
        params: GTMConversionParams = {}
    ): void {
        const consent = this.getCookieConsent();

        if (consent !== 'accepted') {
            console.log('Google Ads tracking skipped - cookies not accepted');
            return;
        }

        if (typeof window === 'undefined' || !window.gtag) {
            console.warn('Google Ads not initialized');
            return;
        }

        try {
            const defaultParams: GTMConversionParams = {
                value: 1.0,
                currency: 'CAD',
                ...params,
            };

            window.gtag('event', 'conversion', {
                send_to: conversionType,
                ...defaultParams,
            });

            console.log('Google Ads conversion tracked:', {
                type: conversionType,
                params: defaultParams,
            });
        } catch (error) {
            console.error('Failed to track Google Ads conversion:', error);
        }
    }

    /**
     * Track page view conversion
     */
    trackPageView(): void {
        console.log('Tracking page view conversion');
        this.trackConversion(GTMConversionType.PAGE_VIEW);
    }

    /**
     * Track first message conversion with unique transaction ID
     */
    trackFirstMessage(assistantName?: string): void {
        const transactionId = `first_msg_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;

        console.log('Tracking first message conversion');
        this.trackConversion(GTMConversionType.FIRST_MESSAGE, {
            transaction_id: transactionId,
            event_label: assistantName,
        });
    }

    /**
     * Track high intent lead conversion
     */
    trackHighIntentLead(assistantName?: string): void {
        console.log('Tracking high intent lead conversion');
        this.trackConversion(GTMConversionType.HIGH_INTENT_LEAD, {
            event_label: assistantName,
        });
    }

    /**
     * Track assistant selection conversion
     */
    trackAssistantSelected(assistantName: string, category?: string): void {
        console.log('Tracking assistant selected conversion');
        this.trackConversion(GTMConversionType.ASSISTANT_SELECTED, {
            event_category: category || 'uncategorized',
            event_label: assistantName,
        });
    }

    /**
     * Track custom event
     */
    trackEvent(eventName: string, params?: any): void {
        const consent = this.getCookieConsent();

        if (consent !== 'accepted') {
            console.log('Google Ads tracking skipped - cookies not accepted');
            return;
        }

        if (typeof window === 'undefined' || !window.gtag) {
            console.warn('Google Ads not initialized');
            return;
        }

        try {
            window.gtag('event', eventName, params);
            console.log('Google Ads event tracked:', { event: eventName, params });
        } catch (error) {
            console.error('Failed to track Google Ads event:', error);
        }
    }

    /**
     * Check if tracking is ready
     */
    isReady(): boolean {
        return this.scriptLoaded && this.getCookieConsent() === 'accepted';
    }
}

// Export singleton instance
export const gtmService = GTMService.getInstance();